import { useState } from 'react';

import {
  ModalPopup,
  Strong,
  Text,
  SelectButton,
  Trash,
  Verify,
  COLORS,
} from '@summer/ui-components';

import { Box } from 'components/box/Box';
import { useSummerHistory } from 'hooks/useSummerHistory';
import { useMutateBorrower } from 'state/server/borrowers';
import { useSelf } from 'state/server/self';

import VerifyBorrowerResidency from './VerifyBorrowerResidency';

const useToggle = (initialState = false) => {
  const [state, setState] = useState(initialState);
  const toggle = () => {
    setState(!state);
  };
  return [state, toggle, setState];
};

const BorrowerAdminPanel = ({ borrower }: any) => {
  const { data: self } = useSelf();

  const releaseResidencyBypass = self.flags['release-residency-bypass'];

  const [showDeleteBorrower, toggleDeleteBorrower] = useToggle();
  const [showResubscribe, toggleResubscribe] = useToggle();
  const [showVerifyResidency, toggleVerifyResidency] = useToggle();

  const actions = [
    {
      enabled: borrower.sendgridGloballyUnsubscribed,
      selectOptions: {
        label: 'Resubscribe to Emails',
        handleClick: toggleResubscribe,
      },
      confirmationModal: () => (
        <ResubscribeBorrower
          borrower={borrower}
          isOpen={showResubscribe}
          togglePopup={toggleResubscribe}
        />
      ),
    },
    {
      enabled:
        releaseResidencyBypass === true &&
        borrower.residencyVerification.isRequired,
      selectOptions: {
        label: 'Verify Residency',
        icon: Verify,
        iconColor: COLORS.jade,
        handleClick: toggleVerifyResidency,
      },
      confirmationModal: () => (
        <VerifyBorrowerResidency
          isOpen={showVerifyResidency}
          togglePopup={toggleVerifyResidency}
          borrowerId={borrower.id}
        />
      ),
    },
    {
      enabled: self.role === 'admin',
      selectOptions: {
        label: 'Delete Borrower',
        icon: Trash,
        iconColor: COLORS.orange,
        handleClick: toggleDeleteBorrower,
      },
      confirmationModal: () => (
        <DeleteBorrower
          borrower={borrower}
          isOpen={showDeleteBorrower}
          togglePopup={toggleDeleteBorrower}
        />
      ),
    },
  ];

  const enabledActions = actions.filter(action => action.enabled);
  const hasAnyOptions = enabledActions.length > 0;

  if (!hasAnyOptions) {
    return null;
  }

  const enabledSelectOptions = enabledActions.map(
    action => action.selectOptions,
  );
  const enabledModals = enabledActions.map(action => action.confirmationModal);

  return (
    <>
      <Box display="inline-block" ml="auto" position="relative" width={224}>
        {/* @ts-expect-error handleClick is a toggle typed boolean | Dispatch */}
        <SelectButton label="Actions" selectOptions={enabledSelectOptions} />
      </Box>

      {enabledModals.map((Modal, index) => (
        <Modal key={index} />
      ))}
    </>
  );
};

const DeleteBorrower = ({ isOpen, togglePopup, borrower }: any) => {
  const { mutate } = useMutateBorrower('delete');
  const { goBack } = useSummerHistory();
  const handleDeleteBorrower = async () => {
    try {
      mutate({ borrowerId: borrower.id });
      // eslint-disable-next-line no-alert
      alert(`Successfully deleted borrower ${borrower.id} `);
      goBack('/borrowers');
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`An error occurred while deleting the borrower: ${e}`);
    }
  };

  return (
    <ModalPopup
      togglePopup={togglePopup}
      isOpen={isOpen}
      handleButtonClick={handleDeleteBorrower}
      buttonText="Yes, delete this borrower"
      component={() => (
        <Box>
          <Text paragraph>
            You are about to delete this borrower
            <br />
            <Strong>Id:</Strong>
            {borrower.id}
            <br />
            <Strong>Email:</Strong>
            {borrower.email}
          </Text>
          <Text paragraph bold color="error">
            This action will delete all data of the borrower permanently
          </Text>
        </Box>
      )}
    />
  );
};

const ResubscribeBorrower = ({ isOpen, togglePopup, borrower }: any) => {
  const { mutate, isPending } = useMutateBorrower('resubscribe');

  const handleResubscribe = async () => {
    try {
      mutate({ borrowerId: borrower.id });
      // eslint-disable-next-line no-alert
      alert(`Successfully resubscribed borrower ${borrower.id} to emails`);
      togglePopup();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`An error occurred while resubscribing the borrower: ${e}`);
    }
  };

  return (
    <ModalPopup
      togglePopup={togglePopup}
      isOpen={isOpen}
      handleButtonClick={handleResubscribe}
      buttonText={isPending ? 'Resubscribing...' : 'Yes, resubscribe'}
      component={() => (
        <Box>
          <Text paragraph>
            Are you sure you want to resubscribe this borrower to Sendgrid
            emails?
          </Text>
          <Text paragraph>
            <Strong>ID:</Strong> {borrower.id}
            <br />
            <Strong>Email:</Strong> {borrower.email}
          </Text>
          <Text paragraph>
            <Strong>
              Please review the following before resubscribing the borrower:
            </Strong>
          </Text>
          <Box as="ul" pl={3}>
            <Text as="li" paragraph>
              Confirm that the borrower has requested to opt back into receiving
              emails from Summer
            </Text>
            <Text as="li" paragraph>
              This will remove them from the global unsubscribe list and they
              will start receiving emails again.
            </Text>
          </Box>
        </Box>
      )}
    />
  );
};

export default BorrowerAdminPanel;
